import { CreateDonationRequestModeEnum } from '@/app/api';
import {
  SingleDonationFormValuesAuthenticatedUser,
  SingleDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/SingleDonationForm/SingleDonationForm';
import { DonationFormBaseValues } from '@/app/components/DonationForm/types';
import { RadioGroupOption } from '@/app/components/FormRadioGroup/FormRadioGroup';
import { Country, DonationFormType } from '@/app/enums';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { useProjectData } from '@/app/providers/ProjectProvider';
import { countryConfig } from '@/app/utils/countryConfig';
import { getDefaultAmountBasedValue } from '@/app/utils/countryConfig/amountBasedTipOptions';
import { useMemo } from 'react';

const getInitialAmountPredesigned = (amountOptions: RadioGroupOption<number>[], amount?: number) => {
  if (!amount) {
    return 600;
  }

  const correspondingOption = amountOptions.find((option) => option.value === amount);

  if (!correspondingOption) {
    return 0;
  }

  return correspondingOption.value;
};

export const useInitialValues = <
  FormValuesType extends DonationFormBaseValues = SingleDonationFormValuesUnauthenticatedUser,
>({
  amountOptions,
  defaultInitialValues,
}: {
  amountOptions: RadioGroupOption<number>[];
  defaultInitialValues?: Partial<FormValuesType>;
}) => {
  const { currentUser } = useCurrentUser();
  const { project } = useProjectData();

  console.log(defaultInitialValues);

  const initialValues = useMemo<
    SingleDonationFormValuesAuthenticatedUser | SingleDonationFormValuesUnauthenticatedUser
  >(() => {
    const nameOnReceipt =
      currentUser?.firstName && currentUser?.lastName
        ? `${currentUser?.firstName} ${currentUser?.lastName}`
        : currentUser?.fullName;

    const amount = getInitialAmountPredesigned(amountOptions, defaultInitialValues?.amount);

    const base = {
      donationMode: CreateDonationRequestModeEnum.Single,
      paymentMethod: countryConfig.charityCollections.defaultPaymentMethod,
      amountPredesigned: getInitialAmountPredesigned(amountOptions, defaultInitialValues?.amount),
      amount: getInitialAmountPredesigned(amountOptions, defaultInitialValues?.amount),
      firstName: currentUser?.firstName ?? '',
      lastName: currentUser?.lastName ?? '',
      certificateOfGoodness: false,
      donationReceipt: currentUser?.wantsDonationReceipt ?? false,
      noNewsletter: false,
      agreedToShareContact: false,
      certificateRecipient: '',
      certificateDonor: '',
      nameOnReceipt: nameOnReceipt ?? '',
      phoneNumber: currentUser?.phoneNumber ?? '',
      streetAndHouseNumber: currentUser?.address?.street ?? '',
      city: currentUser?.address?.city ?? '',
      zipCode: currentUser?.address?.zip ?? '',
      tin: currentUser?.companyLegalId ?? '',
      birthDate: currentUser?.birthdate ?? undefined,
      tipAmount: !project.settings.hasTipsDisabled ? getDefaultAmountBasedValue(amount) : undefined,
      tipPercentage: project.settings.hasTipsDisabled
        ? 0
        : countryConfig.donationFormCommon.defaultTipForAmountBelowBreakpoint,
      country: (currentUser?.address?.country as Country) ?? countryConfig.country,
    };

    if (!currentUser) {
      return {
        ...base,
        email: '',
        ...(defaultInitialValues ?? {}),
        formType: DonationFormType.SINGLE_UNAUTHENTICATED_USER,
      };
    }

    return {
      ...base,
      ...(defaultInitialValues ?? {}),
      formType: DonationFormType.SINGLE_AUTHENTICATED_USER,
    };
  }, [defaultInitialValues, currentUser, getInitialAmountPredesigned]);

  return initialValues;
};
